// Typed client for F2's library routes: trash, folders and transcript search
// (server/src/trash.rs, folders.rs, search.rs). Same style as calendar/client.ts — thin
// wrappers around the shared `api()` helper, one function per route.
import { api } from "../api/client";
import type {
  CreateFolderRequest,
  FolderOut,
  GrackleDetail,
  GrackleOut,
  ListResponse,
  PagedResponse,
  SearchResultOut,
  TrashPage,
} from "../api/types";

// ---- Rename (FR-L2) ----

/** `PATCH /grackles/{id}` with only `title`; the server trims it and allows 1-200 chars. */
export function renameGrackle(grackleId: string, title: string): Promise<GrackleDetail> {
  return api("PATCH", `/grackles/${grackleId}`, { title });
}

// ---- Trash ----

export function listTrash(orgId: string, params?: { cursor?: string | null; limit?: number }): Promise<TrashPage> {
  const qs = new URLSearchParams();
  if (params?.cursor) qs.set("cursor", params.cursor);
  if (params?.limit) qs.set("limit", String(params.limit));
  const s = qs.toString();
  return api("GET", `/orgs/${orgId}/trash${s ? `?${s}` : ""}`);
}

export function restoreGrackle(grackleId: string): Promise<GrackleDetail> {
  return api("POST", `/grackles/${grackleId}/restore`);
}

export function purgeGrackle(grackleId: string): Promise<void> {
  return api("DELETE", `/grackles/${grackleId}/purge`);
}

// ---- Folders ----

export function listFolders(orgId: string, scope?: string): Promise<ListResponse<FolderOut>> {
  const qs = scope ? `?scope=${encodeURIComponent(scope)}` : "";
  return api("GET", `/orgs/${orgId}/folders${qs}`);
}

export function createFolder(orgId: string, body: CreateFolderRequest): Promise<FolderOut> {
  return api("POST", `/orgs/${orgId}/folders`, body);
}

export function renameFolder(orgId: string, folderId: string, name: string): Promise<FolderOut> {
  return api("PATCH", `/orgs/${orgId}/folders/${folderId}`, { name });
}

export function deleteFolder(orgId: string, folderId: string): Promise<void> {
  return api("DELETE", `/orgs/${orgId}/folders/${folderId}`);
}

export function folderGrackles(
  orgId: string,
  folderId: string,
  params?: { cursor?: string | null; limit?: number },
): Promise<PagedResponse<GrackleOut>> {
  const qs = new URLSearchParams();
  if (params?.cursor) qs.set("cursor", params.cursor);
  if (params?.limit) qs.set("limit", String(params.limit));
  const s = qs.toString();
  return api("GET", `/orgs/${orgId}/folders/${folderId}/grackles${s ? `?${s}` : ""}`);
}

export function moveGrackle(grackleId: string, folderId: string | null): Promise<void> {
  return api("POST", `/grackles/${grackleId}/move`, { folder_id: folderId });
}

// ---- Transcript search ----

export interface SearchParams {
  q: string;
  folder?: string;
  owner?: string;
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
}

export function searchGrackles(orgId: string, params: SearchParams): Promise<SearchResultOut[]> {
  const qs = new URLSearchParams({ q: params.q });
  if (params.folder) qs.set("folder", params.folder);
  if (params.owner) qs.set("owner", params.owner);
  if (params.from) qs.set("from", params.from);
  if (params.to) qs.set("to", params.to);
  if (params.limit != null) qs.set("limit", String(params.limit));
  if (params.offset != null) qs.set("offset", String(params.offset));
  return api("GET", `/orgs/${orgId}/search?${qs.toString()}`);
}
